import { useState, useEffect } from "react";

/**
 * Persists a small {view, sort, groupBy, ...} preference blob to localStorage
 * under one key, validating saved values against the allowed lists passed in
 * `options` (unknown/stale saved values fall back to `defaults`).
 *
 * An entry in `options` is either an array of allowed values, or a predicate
 * `(value) => boolean` for prefs whose valid set isn't enumerable -- an array
 * of active filters, or a boolean toggle. A key with no entry in `options` is
 * never restored, so pass a predicate rather than omitting it.
 */
export function useViewPrefs(storageKey, options, defaults) {
  const [prefs, setPrefs] = useState(() => {
    let saved = null;
    try {
      saved = JSON.parse(localStorage.getItem(storageKey));
    } catch {
      saved = null;
    }
    const result = { ...defaults };
    for (const key of Object.keys(defaults)) {
      const allowed = options[key];
      if (!allowed || !saved) continue;
      const ok = typeof allowed === "function"
        ? allowed(saved[key])
        : allowed.includes(saved[key]);
      if (ok) result[key] = saved[key];
    }
    return result;
  });

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(prefs));
  }, [storageKey, prefs]);

  return [prefs, setPrefs];
}
